/**
 * Determina que acciones de factura se muestran en el detalle segun su
 * estado (eInvoiceStatus). Extraido de InvoiceDetailPage.tsx para poder
 * testear la matriz estado -> acciones sin necesidad de renderizar el
 * componente ni de una API real.
 *
 * Valores de eInvoiceStatus (ver DGI_FLOW_MASTER.md / bitacora FE):
 *   0 = Procesado (DGI 0920)
 *   1 = Error
 *   2 = Encolada (DGI 0260)
 *   3 = Procesando
 *   4 = Pendiente de subir
 *   5 = Anulada
 */

export type QuickAction =
  | 'resendEmail'
  | 'printCafe'
  | 'viewCafe'
  | 'viewXml'
  | 'viewDgi'
  | 'viewCfuXml';

export type MenuAction =
  | 'correctionSameCufe'
  | 'correctionNewCufe'
  | 'resendToDgi'
  | 'cancelInvoice'
  | 'creditNote'
  | 'debitNote'
  | 'cloneInvoice';

export interface QuickActionFlags {
  hasXml: boolean;
  hasCafe: boolean;
  hasQrCode: boolean;
}

/**
 * Botones de la tarjeta bajo el QR ("Actions").
 */
export function getQuickActions(
  eInvoiceStatus: number | undefined,
  flags: QuickActionFlags
): QuickAction[] {
  if (eInvoiceStatus === 1) {
    // Factura con error: solo el XML crudo, si existe.
    return flags.hasXml ? ['viewCfuXml'] : [];
  }

  if (eInvoiceStatus === 2) {
    // Encolada: la DGI todavia no proceso la factura, solo tiene sentido
    // ver el XML que se envio. Nada de reenvio de email, CAFE ni "ver en
    // DGI" (no hay nada que consultar en DGI todavia).
    return flags.hasXml ? ['viewXml'] : [];
  }

  // Resto de estados (Procesado, Procesando, Pendiente de subir, Anulada):
  // comportamiento actual sin cambios.
  const actions: QuickAction[] = ['resendEmail', 'printCafe'];
  if (flags.hasCafe) actions.push('viewCafe');
  if (flags.hasXml) actions.push('viewXml');
  if (flags.hasQrCode) actions.push('viewDgi');
  return actions;
}

/**
 * Opciones del menu desplegable "Acciones".
 */
export function getMenuActions(eInvoiceStatus: number | undefined): MenuAction[] {
  if (eInvoiceStatus === 1) {
    // Factura con error: solo opciones de correccion.
    return ['correctionSameCufe', 'correctionNewCufe'];
  }

  if (eInvoiceStatus === 2) {
    // Encolada: unicamente reenviar a DGI. Anular/NC/ND/Clonar no aplican
    // a una factura que la DGI aun no acepto.
    return ['resendToDgi'];
  }

  // Resto de estados: "Reenviar a DGI" solo aplica a facturas Encoladas, no
  // se debe mostrar (ni deshabilitado) para facturas ya Procesadas/Anuladas/etc.
  return ['cancelInvoice', 'creditNote', 'debitNote', 'cloneInvoice'];
}
